import React, { useCallback, useEffect } from "react";
import { PropsWithChildren, useMemo } from "react";
import { apiStore } from "../../api/ApiFactory";
import { RowsPerPageOptions } from "../../helpers/constants/TableConfig";
import { ITableResult } from "../../models/TableModel";
import { ReactTable } from "./ReactTable";
import { TableProps } from "./TableProps";
import { Filters, SortingRule } from "react-table";

export function Table<T extends Record<string, unknown>>(
  props: PropsWithChildren<TableProps<T>>
) {
    const {
        columns,
        name,
        apiGet,
        serverOnChange,
        onAdd,
        onEdit,
        onDelete,
        onPrint,
        onExport,
        extraCommands,
        pageSize,
        pageIndex,
        initialSort,
        initialFilters,
        className,
        inlineLock,
        inlineLockState,
        onInlineEdit,
        ignorePagination,
        hideToolbar,
        hiddenColumns,
    } = props;

    const setPageSize = () => {

        let value = RowsPerPageOptions[0];
        if(pageSize) {
            if(RowsPerPageOptions.filter((x => x == pageSize)).length > 0) {
                value = pageSize;
            }
        }

        return value;
    }

    const [data, setPageData] = React.useState({
        rows: [] as any,
        isLoading: true,
        pageCount: 0
    });

    const [pageDef, setPageDef] = React.useState({
        currentPage: pageIndex ?? 0,
        pageSize: setPageSize(),
        filters: initialFilters ?? [] as Filters<T>,
        sortBy: initialSort ?? [] as SortingRule<T>[]
    });

    const [refreshToken, setRefreshToken] = React.useState(-1);

    useEffect(() => {

        //this useEffect will trigger everytime the table page changes. We only want this block of code to trigger
        //if serverOnChange is set to true or data.isLoading is set to true. 
        //So, if serverOnChange is false we only want to trigger the api call once.

        if(serverOnChange || data.isLoading) {

            let params = new URLSearchParams();

            if(serverOnChange) {
                var page = pageDef.currentPage;
                var pageSize = pageDef.pageSize;
                var sortColumn = pageDef.sortBy.length > 0 ? pageDef.sortBy[0].id : undefined;
                var sortDirection = pageDef.sortBy.length > 0 ? (pageDef.sortBy[0].desc ? "desc" : "asc") : undefined;

                params.append("page", page.toString());
                params.append("pageSize", pageSize.toString());

                //We only allow sorting on one column. If this is ever extended to multiple columns then it will need to be passed in like filters.
                if(sortColumn && sortDirection) {
                    params.append("sortColumn", sortColumn);
                    params.append("sortDirection", sortDirection);
                }

                pageDef.filters.map((item, index) => params.append("filters[" + index + "].id", item.id.toString()));
                pageDef.filters.map((item, index) => params.append("filters[" + index + "].value", item.value.toString()));

            }

            apiStore.tableApi.getResults<T>(apiGet, params).then(
                (response) => {
                    var pageCount = ignorePagination ? 1 : Math.ceil(response.rowCount / pageDef.pageSize!);
    


                    setPageData({
                        rows: response.data,
                        isLoading: false,
                        pageCount: pageCount
                    });
                }
            );
        }

    }, [pageDef, refreshToken]);

    const findRecordById = (rows: any[], id: string | number) => {
        return rows.find(row => row.id === id);
    };

    const handleInlineEdit = async (rowId: string | number, columnId: string, newValue: any) => {
        if (onInlineEdit) {
            
            const record = findRecordById(data.rows, rowId);
            const result = await onInlineEdit(rowId, columnId, newValue, record);

            if (result) {

                setPageData((prevState) => ({
                    ...prevState,
                    rows: prevState.rows.map((row : any) =>
                        row.id === rowId ? { ...row, [columnId]: newValue } : row
                    ),
                }));
            }
        }
    };

    const forceRefresh = useCallback(
        () => {
            setRefreshToken(Math.random())
        },
        []
    );

    //Support for server operations on changes like pagination, sorting, and filtering has not been completed.
    //Calling component should set serverOnChange to false until this feature is complete.
    const manualOp: boolean = serverOnChange === undefined ? true : serverOnChange;
    const lockStatus: boolean = inlineLockState === undefined ? true : inlineLockState;

    return (
        <ReactTable<T>
            name={name}
      		className={className}
            data={data.rows}
            columns={columns}
            manualPagination={manualOp}
            manualSortBy={manualOp}
            manualFilters={manualOp}
            isLoading={data.isLoading}
            pageCount={manualOp ? data.pageCount : undefined}
            forceRefreshHandler={forceRefresh}
            pageChangeHandler={setPageDef}
            initialState={{ 
                pageIndex: ignorePagination ? 0 : pageDef.currentPage, 
                pageSize:  ignorePagination ? (data.rows.length == 0 ? 50 : data.rows.length) : pageDef.pageSize,
                filters: pageDef.filters,
                sortBy: pageDef.sortBy,
                hiddenColumns: hiddenColumns ?? [],
            }}
            onAdd={onAdd}
            onDelete={onDelete}
            onEdit={onEdit}
            onPrint={onPrint}
            extraCommands={extraCommands}
            onExport={onExport}
            inlineLock={inlineLock}
            inlineLockState={lockStatus}
            onInlineEdit={ onInlineEdit !== undefined ? handleInlineEdit : undefined }
            ignorePagination
            hideToolbar={hideToolbar}
        />
    )
}